'use client'

import React, { FC, ReactElement } from 'react'
import axios from 'axios'
import useSwr from 'swr'
import classNames from 'classnames'
import { Input } from '@/components/ui/input'
import { CommandItem } from '@/components/ui/command'
import formatNumber from '@/utils/format-number'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { NETWORK, API_URL } from '@/utils/constants'
import Wallet from '@/utils/wallet'
import { useToast } from '@/components/ui/use-toast'
import featureFlags from '@/utils/feature-flags'
import useWallet from '@/utils/hooks/useWallet'
import InscriptionContent from '@/components/inscription-content'
import { Loading } from '@/components/ui/loading'
import { Inscription } from '@/utils/types/inscription'

import useSelect from '../hooks/useSelect'
import SelectButton from '../components/select-button'
import Invoice from '../components/invoice'
import { isSnipeProtectionOffered } from '@/utils/market/secure-purchase'
import { useSnipeProtection } from '@/utils/market/use-snipe-protection'
import { snipeProtectionInvoiceItem } from '@/components/snipe-protection-toggle'
import {
	assertStandardListingAllowed,
	describeSecureListingResult,
	isolateTargetForFailures,
	listSecureItems
} from '@/utils/market/secure-listing-client'
import {
	availabilityForCollection,
	collectionSlugOf,
	planForProtectedFailure,
	protectedErrorCode,
	protectionAssetFromRow,
	resolveListingPlan,
	SNIPE_TEMPORARILY_UNAVAILABLE
} from '@/utils/market/protection-support'
import { toastListingSuccess } from '@/utils/market/listing-success-toast'

const EMPTY_SHAPE_ACK: Record<string, 'ambiguous_inscription_assets' | 'postage_too_small'> = {}

interface ExternalListing {
	id: string
	collection: string
	price: number
	owner: string
	output: string
}

interface JoinedListing {
	listing: ExternalListing
	inscription: Inscription
}

const ImportListings: FC<{ onNavigate: (v: string) => void; onClose: () => void; args: any }> = (
	props
): ReactElement => {
	const { onClose } = props
	const { toast } = useToast()
	const { exchangeRate } = useExchangeRate()
	const address = Wallet.getTaprootAddress()
	const { data: walletData, isLoading: walletLoading } = useWallet()

	const [error, setError] = React.useState<string | null>(null)
	const [prices, setPrices] = React.useState<Record<string, number>>({})

	// SWR keyed on address — null key suspends until address is set.
	const { data: importListingsResp, isLoading: fetchLoading } = useSwr(
		address ? `${API_URL}/wallet/${address}/import-listings` : null,
		async (url: string) => {
			try {
				const { data } = await axios.get(url)
				return data
			} catch (e: any) {
				setError(e?.response?.data?.message || e?.message || 'Failed to fetch listings')
				return null
			}
		},
		{ revalidateOnFocus: false }
	)
	const externalListings: ExternalListing[] = React.useMemo(
		() => importListingsResp?.listings || [],
		[importListingsResp]
	)
	const walletInscriptions = walletData?.inscriptions

	// Join external listings against wallet inscriptions — only keep items we actually own
	const items = React.useMemo(() => {
		if (!walletInscriptions || !externalListings.length) return []

		const inscriptionMap = new Map<string, Inscription>()
		for (const insc of walletInscriptions) {
			inscriptionMap.set(insc.id, insc)
		}

		const joined: JoinedListing[] = []
		for (const listing of externalListings) {
			const insc = inscriptionMap.get(listing.id)
			if (insc) {
				joined.push({ listing, inscription: insc })
			}
		}
		return joined
	}, [walletInscriptions, externalListings])

	// Set initial prices once items are resolved
	React.useEffect(() => {
		if (!items.length) return
		const initialPrices: Record<string, number> = {}
		for (const { listing } of items) {
			initialPrices[listing.id] = listing.price / 1e8
		}
		requestAnimationFrame(() => {
			setPrices(initialPrices)
		})
	}, [items])

	const ids = React.useMemo(() => items.map((e) => e.listing.id), [items])
	const { selected, setSelected, onSelect, onSelectAll, allSelected } = useSelect(ids)

	React.useEffect(() => {
		if (ids.length > 0) {
			requestAnimationFrame(() => {
				setSelected(new Set(ids))
			})
		}
	}, [ids, setSelected])

	const handlePriceChange = React.useCallback(
		(id: string) => (evt: any) => {
			const value = evt.target.value
			setPrices((prev) => ({ ...prev, [id]: parseFloat(value) || 0 }))
		},
		[]
	)

	const { selectedCount, totalPrice, totalPriceUsd } = React.useMemo(() => {
		let total = 0
		let count = 0

		for (const [id] of Array.from(selected.entries())) {
			total += (prices[id] || 0) * 1e8
			count++
		}

		return {
			selectedCount: count,
			totalPrice: formatSatoshis(total),
			totalPriceUsd: formatNumber(((total / 1e8) * exchangeRate)?.toFixed(2))
		}
	}, [selected, prices, exchangeRate])

	// Imports span collections. The probe is for the API; each item applies its own slug.
	const snipeProtection = useSnipeProtection('ordinal')
	const selectedKey = [...selected].sort().join('\n')
	const [shapeState, setShapeState] = React.useState<{
		key: string
		prompt: string | null
		ack: Record<string, 'ambiguous_inscription_assets' | 'postage_too_small'>
	}>({ key: '', prompt: null, ack: {} })
	const shapePrompt = shapeState.key === selectedKey ? shapeState.prompt : null
	const shapeAck = shapeState.key === selectedKey ? shapeState.ack : EMPTY_SHAPE_ACK
	const setShapePrompt = React.useCallback(
		(message: string | null) => {
			setShapeState((prev) => ({
				key: selectedKey,
				prompt: message,
				ack: prev.key === selectedKey ? prev.ack : {}
			}))
		},
		[selectedKey]
	)
	const setShapeAck = React.useCallback(
		(
			update: (
				prev: Record<string, 'ambiguous_inscription_assets' | 'postage_too_small'>
			) => Record<string, 'ambiguous_inscription_assets' | 'postage_too_small'>
		) => {
			setShapeState((prev) => {
				const prevAck = prev.key === selectedKey ? prev.ack : {}
				return {
					key: selectedKey,
					prompt: prev.key === selectedKey ? prev.prompt : null,
					ack: update(prevAck)
				}
			})
		},
		[selectedKey]
	)

	const importPreview = React.useMemo(() => {
		const chosen = items.filter(({ listing }) => selected.has(listing.id))
		if (chosen.length === 0) return { shield: false, note: null as string | null, shapeOnly: false }
		let shield = false
		let note: string | null = null
		let shape = 0
		let other = 0
		for (const item of chosen) {
			const slug = collectionSlugOf(item.inscription, item.listing.collection)
			let asset = protectionAssetFromRow(
				{ ...item.inscription, collection: slug ? { slug } : item.inscription.collection },
				slug
			)
			const ack = shapeAck[item.listing.id]
			if (ack === 'ambiguous_inscription_assets') asset = { ...asset, sharedUtxoCount: 2 }
			if (ack === 'postage_too_small') asset = { ...asset, postageSats: 1 }
			const plan = resolveListingPlan({
				asset,
				availability: availabilityForCollection(snipeProtection.status, slug),
				caps: snipeProtection.caps,
				shapeConfirmed: false
			})
			if (plan.action === 'protect') {
				shield = true
				other += 1
			} else if (plan.action === 'confirm') {
				shape += 1
				note = note || plan.message
			} else if (plan.action === 'standard') {
				if (plan.shape) shape += 1
				else other += 1
				note = note || plan.note
			} else {
				other += 1
				note = note || plan.message
			}
		}
		return { shield, note, shapeOnly: shape > 0 && other === 0 }
	}, [items, selected, shapeAck, snipeProtection.status, snipeProtection.caps])
	const confirmingShape = importPreview.shapeOnly || !!shapePrompt

	const handleSubmit = React.useCallback(async (sellerConfirmed = false) => {
		try {
			const selectedIds: string[] = []
			const finalPrices: number[] = []

			for (const [id] of Array.from(selected.entries())) {
				selectedIds.push(id)
				finalPrices.push(parseInt(((prices[id] || 0) * 1e8).toFixed(0)))
			}

			const selectedItems = items.filter(({ listing }) => selected.has(listing.id))
			const listingTarget = {
				inscriptionIds: selectedItems.map((item) => item.inscription?.id || item.listing.id),
				collectionSlugs: selectedItems.map(
					(item) => item.inscription?.collection?.slug || item.listing.collection
				)
			}

			let availability = snipeProtection.apiStatus
			let caps = snipeProtection.caps
			if (availability === 'loading' || availability === 'probe_failed') {
				const fresh = await isSnipeProtectionOffered('ordinal')
				availability = fresh.status
				caps = fresh.caps
			}

			const planned = selectedIds.map((id, index) => {
				const item = items.find((row) => row.listing.id === id)
				const slug = collectionSlugOf(item?.inscription, item?.listing.collection)
				let asset = protectionAssetFromRow(
					{
						...item?.inscription,
						collection: slug ? { slug } : item?.inscription?.collection
					},
					slug
				)
				const ack = shapeAck[id]
				if (ack === 'ambiguous_inscription_assets') asset = { ...asset, sharedUtxoCount: 2 }
				if (ack === 'postage_too_small') asset = { ...asset, postageSats: 1 }
				return {
					id,
					price: finalPrices[index],
					item,
					asset,
					slug,
					plan: resolveListingPlan({
						asset,
						availability: availabilityForCollection(availability, slug),
						caps,
						shapeConfirmed: sellerConfirmed
					})
				}
			})
			let standard = planned.filter((entry) => entry.plan.action === 'standard')
			const protect = planned.filter((entry) => entry.plan.action === 'protect')
			const confirm = planned.filter((entry) => entry.plan.action === 'confirm')
			const blocked = planned.filter((entry) => entry.plan.action === 'block')
			if (confirm.length > 0 && !sellerConfirmed) {
				const message = confirm[0].plan.action === 'confirm' ? confirm[0].plan.message : ''
				setShapePrompt(message)
				toast({ title: 'Confirm listing without snipe protection', description: message })
				return
			}
			if (blocked.length > 0 && protect.length === 0 && standard.length === 0) {
				const message =
					blocked[0].plan.action === 'block' ? blocked[0].plan.message : SNIPE_TEMPORARILY_UNAVAILABLE
				toast({ title: 'Error', description: message, variant: 'destructive' })
				return
			}

			if (protect.length > 0) {
				try {
					const result = await listSecureItems(
						protect.map((entry) => ({
							inscriptionId: entry.id,
							outpoint:
								(entry.item?.inscription as { outpoint?: string } | undefined)?.outpoint || entry.id,
							priceSats: entry.price,
							protocol: entry.asset.protocol || 'ordinal',
							inscription: {
								...entry.item?.inscription,
								id: entry.item?.inscription?.id || entry.id,
								collection: entry.slug ? { slug: entry.slug } : entry.item?.inscription?.collection
							}
						}))
					)
					let confirmMessage: string | null = null
					let blockMessage: string | null = null
					const shapeIds: Record<string, 'ambiguous_inscription_assets' | 'postage_too_small'> = {}
					for (const failure of result.failures) {
						const next = planForProtectedFailure(failure.code, {
							shapeConfirmed: sellerConfirmed,
							caps,
							provenProtocol: failure.protocol
						})
						const entry =
							protect.find(
								(item) =>
									item.id === failure.outpoint ||
									(item.item?.inscription as { outpoint?: string } | undefined)?.outpoint ===
										failure.outpoint
							) || (protect.length === 1 ? protect[0] : undefined)
						if (next.action === 'confirm' && entry && !sellerConfirmed) {
							if (
								failure.code === 'ambiguous_inscription_assets' ||
								failure.code === 'postage_too_small'
							) {
								shapeIds[entry.id] = failure.code
							}
							confirmMessage = next.message
							continue
						}
						if (next.action === 'standard' && entry) {
							standard.push({
								...entry,
								asset: failure.protocol ? { ...entry.asset, protocol: failure.protocol } : entry.asset
							})
							continue
						}
						blockMessage =
							(next.action === 'block' && next.message) ||
							failure.message ||
							SNIPE_TEMPORARILY_UNAVAILABLE
					}
					if (Object.keys(shapeIds).length > 0) {
						setShapeAck((prev) => ({ ...prev, ...shapeIds }))
						setShapePrompt(confirmMessage)
					}
					if (confirmMessage && !sellerConfirmed) {
						if (result.listed > 0) {
							toastListingSuccess({
								title: 'Partially listed',
								description: `You listed ${result.listed} with snipe protection.`,
								count: result.listed,
								...listingTarget
							})
						}
						toast({ title: 'Confirm listing without snipe protection', description: confirmMessage })
						return
					}
					if (result.listed > 0 && standard.length === 0 && !blockMessage) {
						const summary = describeSecureListingResult(result, 'inscriptions')
						toastListingSuccess({
							title: summary.title,
							description: summary.description,
							isolate: isolateTargetForFailures(
								result.failures,
								protect.map((entry) => ({
									inscriptionId: entry.item?.inscription?.id || entry.id,
									outpoint: (entry.item?.inscription as { outpoint?: string } | undefined)?.outpoint
								})),
								listingTarget.collectionSlugs?.[0] ?? null
							),
							count: result.listed,
							...listingTarget
						})
						if (!summary.failed) onClose()
						return
					}
					if (blockMessage && standard.length === 0) {
						toast({ title: 'Error', description: blockMessage, variant: 'destructive' })
						return
					}
					if (blockMessage) {
						toast({ title: 'Error', description: blockMessage, variant: 'destructive' })
					}
				} catch (e) {
					const code = protectedErrorCode(e)
					const next = code
						? planForProtectedFailure(code, { shapeConfirmed: sellerConfirmed, caps })
						: { action: 'block' as const, message: '' }
					if (next.action === 'confirm' && !sellerConfirmed && code) {
						if (code === 'ambiguous_inscription_assets' || code === 'postage_too_small') {
							const shapeIds: Record<string, typeof code> = {}
							for (const entry of protect) shapeIds[entry.id] = code
							setShapeAck((prev) => ({ ...prev, ...shapeIds }))
						}
						setShapePrompt(next.message)
						toast({ title: 'Confirm listing without snipe protection', description: next.message })
						return
					}
					toast({
						title: 'Error',
						description:
							(next.action === 'block' && next.message) ||
							(next.action === 'standard' && next.note) ||
							(next.action === 'confirm' && next.message) ||
							(e as { message?: string })?.message ||
							SNIPE_TEMPORARILY_UNAVAILABLE,
						variant: next.action === 'standard' ? 'default' : 'destructive'
					})
					return
				}
			}

			if (standard.length === 0) return
			assertStandardListingAllowed(
				standard.map((entry) => entry.asset),
				caps
			)
			const standardIds = standard.map((entry) => entry.id)
			const standardPrices = standard.map((entry) => entry.price)

			const psbt = await Wallet.getEscrows(standardIds, standardPrices, {
				forceBulk: featureFlags('app::disable_bulk')
			})

			let totalCreatedEscrows = 0
			let totalSkippedInputs = 0
			let skippedInputs: any[] = []

			if (Array.isArray(psbt)) {
				for (const e of psbt) {
					const { rawtx } = await Wallet.signEscrow(e)
					const { data: resData } = await axios.post(`${API_URL}/market/escrow-bulk`, {
						psbt: rawtx
					})
					const summary = Wallet.assertEscrowResponse(resData)
					totalCreatedEscrows += summary.createdEscrows
					totalSkippedInputs += summary.skippedInputsCount
					skippedInputs = skippedInputs.concat(summary.skippedInputs || [])
				}
			} else {
				const { rawtx } = await Wallet.signEscrow(psbt)
				const { data: resData } = await axios.post(`${API_URL}/market/escrow-bulk`, {
					psbt: rawtx
				})
				const summary = Wallet.assertEscrowResponse(resData)
				totalCreatedEscrows += summary.createdEscrows
				totalSkippedInputs += summary.skippedInputsCount
				skippedInputs = skippedInputs.concat(summary.skippedInputs || [])
			}

			if (totalCreatedEscrows <= 0) {
				throw new Error('No escrows were created')
			}

			if (totalSkippedInputs > 0) {
				const skippedSummary = Wallet.formatEscrowSkips(skippedInputs)
				toastListingSuccess({
					title: 'Partially Listed',
					description: `Created ${formatNumber(
						totalCreatedEscrows
					)} escrows. Skipped ${formatNumber(totalSkippedInputs)} inputs${
						skippedSummary ? `: ${skippedSummary}` : '.'
					}.`,
					count: totalCreatedEscrows,
					...listingTarget
				})
			} else {
				toastListingSuccess({
					title: 'Success!',
					description: `You listed ${formatNumber(standardIds.length)} inscriptions`,
					count: standardIds.length,
					...listingTarget
				})
			}
			onClose()
		} catch (e: any) {
			toast({
				title: 'Error',
				description: e?.response?.data?.message || e?.message || 'Unable to list item',
				variant: 'destructive'
			})
		}
	}, [
		onClose,
		selected,
		prices,
		toast,
		items,
		snipeProtection.apiStatus,
		snipeProtection.caps,
		shapeAck,
		setShapeAck,
		setShapePrompt
	])

	const isDisabledForNoPrice = totalPrice === '0'

	const renderListing = React.useCallback(
		({ listing, inscription }: JoinedListing) => {
			const isSelected = selected.has(listing.id)
			const price = prices[listing.id]

			return (
				<CommandItem
					key={listing.id}
					value={listing.id}
					onSelect={() => onSelect(listing.id)}
					className="data=[cmdk-item]:py-0.5"
				>
					<div className="flex justify-between w-full">
						<div className="flex gap-2 items-center">
							<SelectButton onSelect={() => onSelect(listing.id)} selected={isSelected} />
							<InscriptionContent inscription={inscription} className="size-6" />
							{inscription?.meta?.name || inscription?.collection?.name || `#${inscription?.num}`}
						</div>
						<div className="flex gap-2 items-center">
							<Input
								value={!isSelected || !price ? '' : price}
								placeholder="--"
								onClick={(evt) => isSelected && evt.stopPropagation()}
								onKeyDown={(evt) => isSelected && evt.stopPropagation()}
								onChange={handlePriceChange(listing.id)}
								type="number"
								step={0.0001}
								className={classNames(
									'text-xs py-0 px-2 h-7 w-24 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
									{ ['text-muted-foreground']: !price }
								)}
							/>
						</div>
					</div>
				</CommandItem>
			)
		},
		[selected, prices, onSelect, handlePriceChange]
	)

	const loading = fetchLoading || walletLoading

	if (loading) {
		return (
			<div className="flex justify-center pt-8">
				<Loading size={16} />
			</div>
		)
	}

	if (error) {
		return <p className="text-xs text-red-500 text-center pt-8">{error}</p>
	}

	if (items.length === 0) {
		return (
			<p className="text-xs text-muted-foreground text-center pt-8">No external listings found.</p>
		)
	}

	return (
		<>
			<div className="flex justify-between items-center px-2 py-3">
				<div className="flex gap-2 items-center">
					<SelectButton selected={allSelected} onSelect={onSelectAll} />
					<p className="text-xs text-muted-foreground select-none">
						{items.length} external listing{items.length !== 1 ? 's' : ''}
					</p>
				</div>
				<p className="text-xs text-muted-foreground select-none">Price ({NETWORK})</p>
			</div>
			{items.map(renderListing)}
			<Invoice
				open={selected.size > 0}
				onSubmit={() => handleSubmit(confirmingShape)}
				disabled={isDisabledForNoPrice}
				action={
					isDisabledForNoPrice
						? 'Please set a price'
						: confirmingShape
						? 'Confirm list without snipe protection'
						: `List ${formatNumber(selectedCount)} inscriptions`
				}
				items={[
					{ title: 'You Receive', value: `${totalPrice} ${NETWORK} / $${totalPriceUsd}` },
					...snipeProtectionInvoiceItem(importPreview.shield, importPreview.note)
				]}
			/>
		</>
	)
}

export default ImportListings
